
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ChevronRight, CheckCircle2, AlertCircle, Download, FolderKanban, Unlock, Pencil, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';
import { toast } from 'sonner';
import { fmt, PAYROLL_STAGES, stageColors, normalizeStage } from './constants';
import { SearchableSelect } from '@/components/SearchableSelect';
import { ClosedPeriodRecovery } from './ClosedPeriodRecovery';
import { useAuth } from '@/contexts/AuthContext';

export function PayrollTab() {
  const { effectiveRole } = useAuth();
  const isOwner = effectiveRole === 'Owner';
  const isAdminOrOwner = effectiveRole === 'Administrator' || effectiveRole === 'Owner';
  const [overrideDialog, setOverrideDialog] = useState(null); // { employee_id, employee_name, current_amount, amount, reason }
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [timingInfo, setTimingInfo] = useState(null);
  const [blockingEntries, setBlockingEntries] = useState(null);
  const [approvingBlock, setApprovingBlock] = useState(false);
  const [ackDialog, setAckDialog] = useState(null); // { code, message, sample, count, opts }
  const [reopenDialog, setReopenDialog] = useState(false);
  const [reopenReason, setReopenReason] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/payroll-periods').then(r => { setPeriods(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadSummary = useCallback(async (ppid) => {
    setSummaryLoading(true);
    try {
      const r = await api.get(`/payroll-periods/${ppid}/summary`);
      setSummary(r.data);
    } catch { toast.error("Failed to load summary"); }
    setSummaryLoading(false);
  }, []);

  const openOverrideDialog = (emp) => {
    setOverrideDialog({
      employee_id: emp.employee_id,
      employee_name: emp.employee_name,
      current_amount: emp.gross_pay,
      amount: String(emp.gross_pay ?? ''),
      reason: '',
    });
  };

  const submitOverride = async () => {
    if (!overrideDialog || !selectedPeriod) return;
    const amount = parseFloat(overrideDialog.amount);
    if (isNaN(amount) || amount < 0) return toast.error('Amount must be a non-negative number');
    if (!overrideDialog.reason.trim()) return toast.error('Reason is required');
    try {
      await api.post(`/payroll-periods/${selectedPeriod.id}/overrides/${overrideDialog.employee_id}`, {
        amount, reason: overrideDialog.reason,
      });
      toast.success(`Override applied for ${overrideDialog.employee_name}`);
      setOverrideDialog(null);
      loadSummary(selectedPeriod.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set override');
    }
  };

  const clearOverride = async (employee_id) => {
    if (!selectedPeriod) return;
    if (!window.confirm('Clear this payroll override? The employee will revert to their normal calculated amount.')) return;
    try {
      await api.delete(`/payroll-periods/${selectedPeriod.id}/overrides/${employee_id}`);
      toast.success('Override cleared');
      loadSummary(selectedPeriod.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clear override');
    }
  };

  useEffect(() => {
    if (selectedPeriod) {
      loadSummary(selectedPeriod.id);
      api.get(`/payroll-periods/timing-info?ppid=${selectedPeriod.id}`).then(r => setTimingInfo(r.data)).catch(() => setTimingInfo(null));
      // Load blocking entries if not in final stage
      if (normalizeStage(selectedPeriod.stage) !== 'Paid / Distributed') {
        api.get(`/payroll-periods/${selectedPeriod.id}/blocking-entries`).then(r => setBlockingEntries(r.data)).catch(() => setBlockingEntries(null));
      } else {
        setBlockingEntries(null);
      }
    }
  }, [selectedPeriod, loadSummary]);

  const createPeriod = async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const r = await api.post('/payroll-periods', { pay_period_start: today });
      toast.success("Payroll period created");
      load();
      setSelectedPeriod(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create period");
    }
  };

  const advancePeriod = async (opts = {}) => {
    if (!selectedPeriod) return;
    try {
      const body = { ...opts };
      const r = await api.post(`/payroll-periods/${selectedPeriod.id}/advance`, body);
      toast.success(`Advanced to: ${r.data.stage}`);
      setSelectedPeriod(r.data);
      setBlockingEntries(null);
      setAckDialog(null);
      load();
      loadSummary(r.data.id);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Backend returns a structured 409 for soft blocks that the user can acknowledge + retry
      if (e.response?.status === 409 && detail && typeof detail === 'object' && detail.code) {
        setAckDialog({ ...detail, opts });
        return;
      }
      toast.error(detail || "Failed to advance", { duration: 8000 });
    }
    setConfirmDialog(null);
  };

  const reloadBlocking = useCallback(async (ppid) => {
    try {
      const r = await api.get(`/payroll-periods/${ppid}/blocking-entries`);
      setBlockingEntries(r.data);
    } catch {
      setBlockingEntries(null);
    }
  }, []);

  const approveBlocking = async (items) => {
    if (!selectedPeriod) return;
    setApprovingBlock(true);
    try {
      const r = await api.post(`/payroll-periods/${selectedPeriod.id}/approve-entries`, items ? { items } : {});
      const d = r.data || {};
      const parts = [];
      if (d.approved) parts.push(`${d.approved} approved`);
      if (d.already_approved) parts.push(`${d.already_approved} already approved`);
      if (d.skipped) parts.push(`${d.skipped} skipped`);
      const msg = parts.join(', ') || 'Nothing to approve';
      if (d.owed_after_disbursement > 0) {
        toast.warning(`${msg}. ${d.owed_after_disbursement} belong to an already-paid period, so money is now owed.`, { duration: 8000 });
      } else {
        toast.success(msg);
      }
      if ((d.skipped || 0) > 0 && Array.isArray(d.skipped_items)) {
        const reasons = [...new Set(d.skipped_items.map(s => s.reason).filter(Boolean))];
        if (reasons.length) toast.info(`Skipped: ${reasons.join('; ')}`, { duration: 7000 });
      }
      await reloadBlocking(selectedPeriod.id);
      loadSummary(selectedPeriod.id);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve entries', { duration: 8000 });
    }
    setApprovingBlock(false);
  };

  const handleAdvance = () => {
    if (!selectedPeriod) return;    const normalizedStage = normalizeStage(selectedPeriod.stage);
    if (normalizedStage === "Pending Review") {
      setConfirmDialog("owner");
    } else {
      advancePeriod();
    }
  };

  const reopenPeriod = async () => {
    if (!selectedPeriod) return;
    if (reopenReason.trim().length < 10) {
      return toast.error('Reason must be at least 10 characters');
    }
    try {
      const r = await api.post(`/payroll-periods/${selectedPeriod.id}/reopen`, { reason: reopenReason.trim() });
      toast.success(`Reopened. Stage reverted to ${r.data.stage}`);
      setSelectedPeriod(r.data);
      setReopenDialog(false);
      setReopenReason('');
      load();
      loadSummary(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reopen');
    }
  };

  // Extract the {detail: "..."} message from an axios error whose
  // responseType is 'blob' (the error body comes back as a Blob, so we
  // need to read+parse it before showing the toast).
  const readBlobError = async (e) => {
    const blob = e.response?.data;
    if (blob && typeof blob.text === 'function') {
      try {
        const text = await blob.text();
        const parsed = JSON.parse(text);
        if (parsed?.detail) return parsed.detail;
        return text.slice(0, 200);
      } catch { /* not JSON, fall through */ }
    }
    return e.response?.data?.detail || e.message || 'Failed to export PDF';
  };

  const [exporting, setExporting] = useState(false);
  const exportPdf = async () => {
    if (!selectedPeriod) return;
    setExporting(true);
    try {
      const r = await api.get(`/payroll-periods/${selectedPeriod.id}/export-pdf`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const filename = `Payroll_Summary_${selectedPeriod.pay_period_start}_to_${selectedPeriod.pay_period_end}.pdf`;
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, filename);
      } else {
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = filename;
        a.setAttribute('target', '_blank');
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 250);
      }
      toast.success("PDF downloaded");
    } catch (e) {
      const msg = await readBlobError(e);
      toast.error(msg);
    }
    setExporting(false);
  };

  // Project-based export
  const [projectExportOpen, setProjectExportOpen] = useState(false);
  const [projectExportProjectId, setProjectExportProjectId] = useState('');
  const [projectExportMonth, setProjectExportMonth] = useState(new Date().toISOString().slice(0, 7));
  const [projectExportLoading, setProjectExportLoading] = useState(false);
  const [projects, setProjects] = useState([]);

  const openProjectExport = () => {
    if (projects.length === 0) {
      api.get('/projects').then(r => setProjects(r.data)).catch(() => {});
    }
    setProjectExportOpen(true);
  };

  const exportByProject = async () => {
    if (!projectExportProjectId || !projectExportMonth) return toast.error('Select a project and month');
    setProjectExportLoading(true);
    try {
      const r = await api.get(`/payroll/export-by-project?project_id=${projectExportProjectId}&billable_month=${projectExportMonth}`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `Payroll_Project_${projectExportMonth}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); window.URL.revokeObjectURL(url); }, 250);
      toast.success("Project payroll PDF downloaded");
      setProjectExportOpen(false);
    } catch (e) {
      const msg = await readBlobError(e);
      toast.error(msg);
    }
    setProjectExportLoading(false);
  };

  const stageIdx = selectedPeriod ? PAYROLL_STAGES.indexOf(normalizeStage(selectedPeriod.stage)) : -1;
  const isFinal = stageIdx >= PAYROLL_STAGES.length - 1;

  return (
    <div className="space-y-6">
      <ClosedPeriodRecovery onChanged={() => { load(); if (selectedPeriod) loadSummary(selectedPeriod.id); }} />
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-lg" data-testid="payroll-pipeline-heading">Payroll Pipeline</h2>
        <Button onClick={createPeriod} size="sm" data-testid="create-payroll-period-btn">
          New Payroll Period
        </Button>
      </div>

      {loading ? (
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      ) : periods.length === 0 ? (
        <Card className="border-border bg-card shadow-none">
          <CardContent className="p-6 text-center text-muted-foreground text-sm">
            No payroll periods created yet. Click &quot;New Payroll Period&quot; to start.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Period List */}
          <div className="space-y-2">
            {periods.map(pp => (
              <Card
                key={pp.id}
                className={`border-border bg-card shadow-none cursor-pointer transition-colors hover:bg-secondary/40 ${selectedPeriod?.id === pp.id ? 'ring-1 ring-primary' : ''}`}
                onClick={() => setSelectedPeriod(pp)}
                data-testid={`payroll-period-${pp.pay_period_start}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold">{pp.pay_period_start} - {pp.pay_period_end}</p>
                      <Badge className={`text-xs mt-1 border ${stageColors[pp.stage] || ''}`}>{pp.stage}</Badge>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Period Detail */}
          <div className="lg:col-span-2">
            {!selectedPeriod ? (
              <Card className="border-border bg-card shadow-none">
                <CardContent className="p-6 text-center text-muted-foreground text-sm">
                  Select a payroll period to view details
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Stage Progress */}
                <Card className="border-border bg-card shadow-none">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-1 mb-2">
                      {PAYROLL_STAGES.map((stage, idx) => (
                        <div key={stage} className="flex items-center flex-1">
                          <div className={`flex-1 h-2 rounded-full ${idx <= stageIdx ? 'bg-primary' : 'bg-secondary'}`} />
                          {idx < PAYROLL_STAGES.length - 1 && <div className="w-1" />}
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-1 mb-4">
                      {PAYROLL_STAGES.map((stage, idx) => (
                        <div key={stage} className="flex-1 text-center">
                          <p className={`text-xs leading-tight ${idx <= stageIdx ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
                            {stage}
                          </p>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Current Stage</p>
                        <Badge className={`text-xs mt-1 border ${stageColors[selectedPeriod.stage] || ''}`} data-testid="payroll-current-stage">
                          {selectedPeriod.stage}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={exportPdf} disabled={exporting} data-testid="export-payroll-pdf-btn">
                          <Download className="h-3 w-3 mr-1" /> {exporting ? 'Exporting...' : 'Export PDF'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={openProjectExport} data-testid="export-by-project-btn">
                          <FolderKanban className="h-3 w-3 mr-1" /> Export by Project
                        </Button>
                        {!isFinal && (
                          <Button size="sm" onClick={handleAdvance} data-testid="advance-payroll-btn">
                            Advance to Next Stage <ChevronRight className="h-3 w-3 ml-1" />
                          </Button>
                        )}
                        {isFinal && isOwner && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                            onClick={() => { setReopenReason(''); setReopenDialog(true); }}
                            data-testid="reopen-payroll-btn"
                          >
                            <Unlock className="h-3 w-3 mr-1" /> Reopen
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Timing Warnings */}
                {timingInfo && timingInfo.past_processing_cutoff && normalizeStage(selectedPeriod.stage) === "Awaiting Time / Processing" && (
                  <Card className="border-rose-500/30 bg-rose-500/5 shadow-none" data-testid="payroll-cutoff-warning">
                    <CardContent className="p-3 flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-rose-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-rose-400">Processing Cutoff Passed</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          The Monday 10:00 AM EST processing cutoff has passed. Payroll should be advanced urgently.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}
                {timingInfo && !timingInfo.pay_period_ended && normalizeStage(selectedPeriod.stage) === "Awaiting Time / Processing" && (
                  <Card className="border-amber-500/30 bg-amber-500/5 shadow-none" data-testid="payroll-period-active-notice">
                    <CardContent className="p-3 flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-amber-400">Pay Period Still Active</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Payroll cannot be advanced until 11:59 PM EST on {timingInfo.pay_period_end}.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Blocking Entries Warning */}
                {blockingEntries && (blockingEntries.timesheets?.length > 0 || blockingEntries.reimbursements?.length > 0) && (
                  <Card className="border-amber-500/30 bg-amber-500/5 shadow-none" data-testid="blocking-entries-card">
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-amber-400 shrink-0" />
                          <p className="text-sm font-semibold text-amber-400">
                            {(blockingEntries.timesheets?.length || 0) + (blockingEntries.reimbursements?.length || 0)} Blocking Entries
                          </p>
                        </div>
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => approveBlocking(null)}
                          disabled={approvingBlock}
                          data-testid="approve-all-blocking-btn"
                        >
                          <CheckCircle2 className="h-3 w-3 mr-1" /> {approvingBlock ? 'Approving...' : 'Approve all'}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">These entries must be Approved or resolved before advancing payroll. Approving here clears them so you can advance.</p>
                      <div className="space-y-1 max-h-[160px] overflow-y-auto">
                        {blockingEntries.timesheets?.map(t => (
                          <div key={t.id} className="flex items-center justify-between gap-2 text-xs bg-background/50 rounded px-2 py-1" data-testid={`blocking-ts-${t.id}`}>
                            <span className="text-foreground flex-1 truncate">{t.user_name}</span>
                            <span className="text-muted-foreground">{t.date}</span>
                            <Badge variant="outline" className={`text-xs ${t.status === 'Draft' ? 'text-zinc-400 border-zinc-500/30' : 'text-blue-400 border-blue-500/30'}`}>
                              {t.status}{t.rejection_note ? ' (Rejected)' : ''}
                            </Badge>
                            <span className="text-muted-foreground w-10 text-right">{t.hours || 0}h</span>
                            <Button size="sm" variant="ghost" className="h-6 text-xs px-2 text-emerald-400 hover:text-emerald-300" onClick={() => approveBlocking([{ type: 'timesheet', id: t.id }])} disabled={approvingBlock} data-testid={`approve-blocking-ts-${t.id}`}>
                              Approve
                            </Button>
                          </div>
                        ))}
                        {blockingEntries.reimbursements?.map(r => (
                          <div key={r.id} className="flex items-center justify-between gap-2 text-xs bg-background/50 rounded px-2 py-1" data-testid={`blocking-re-${r.id}`}>
                            <span className="text-foreground flex-1 truncate">{r.user_name}</span>
                            <span className="text-muted-foreground">{r.date || r.expense_date}</span>
                            <Badge variant="outline" className="text-xs text-orange-400 border-orange-500/30">Reimb. {r.status}</Badge>
                            <span className="text-muted-foreground w-14 text-right">${r.amount?.toFixed(2) || '0.00'}</span>
                            <Button size="sm" variant="ghost" className="h-6 text-xs px-2 text-emerald-400 hover:text-emerald-300" onClick={() => approveBlocking([{ type: 'reimbursement', id: r.id }])} disabled={approvingBlock} data-testid={`approve-blocking-re-${r.id}`}>
                              Approve
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Summary */}
                {summaryLoading ? (
                  <div className="animate-pulse text-muted-foreground text-sm">Loading summary...</div>
                ) : summary ? (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      <Card className="border-border bg-card shadow-none">
                        <CardContent className="p-3 text-center">
                          <p className="text-xs uppercase tracking-wider text-muted-foreground">Total Payroll</p>
                          <p className="text-xl font-bold mt-1 text-primary tabular-nums" data-testid="total-payroll">{fmt(summary.total_payroll)}</p>
                        </CardContent>
                      </Card>
                      <Card className="border-border bg-card shadow-none">
                        <CardContent className="p-3 text-center">
                          <p className="text-xs uppercase tracking-wider text-muted-foreground">Employees</p>
                          <p className="text-xl font-bold mt-1 tabular-nums">{summary.employees?.length || 0}</p>
                        </CardContent>
                      </Card>
                      <Card className="border-border bg-card shadow-none">
                        <CardContent className="p-3 text-center">
                          <p className="text-xs uppercase tracking-wider text-muted-foreground">All Resolved</p>
                          <p className="text-xl font-bold mt-1">
                            {summary.all_resolved ? (
                              <CheckCircle2 className="h-6 w-6 text-emerald-400 mx-auto" />
                            ) : (
                              <AlertCircle className="h-6 w-6 text-amber-400 mx-auto" />
                            )}
                          </p>
                        </CardContent>
                      </Card>
                    </div>

                    <Card className="border-border bg-card shadow-none">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-heading">Employee Breakdown</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow className="border-border hover:bg-transparent">
                              <TableHead className="text-xs uppercase tracking-wider">Employee</TableHead>
                              <TableHead className="text-xs uppercase tracking-wider">Rate</TableHead>
                              <TableHead className="text-xs uppercase tracking-wider text-right">Approved Hrs</TableHead>
                              <TableHead className="text-xs uppercase tracking-wider text-right">Gross Pay</TableHead>
                              <TableHead className="text-xs uppercase tracking-wider text-right">Reimb.</TableHead>
                              <TableHead className="text-xs uppercase tracking-wider text-right">Total</TableHead>
                              <TableHead className="text-xs uppercase tracking-wider text-center">Status</TableHead>
                              {isAdminOrOwner && <TableHead className="text-xs uppercase tracking-wider text-center w-20">Override</TableHead>}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {summary.employees?.filter(e => e.total_hours > 0 || e.total_reimbursements > 0 || e.paycheck_total > 0 || (e.company_card_reimbursements || 0) > 0).map(emp => (
                              <TableRow key={emp.employee_id} className="border-border" data-testid={`payroll-emp-${emp.employee_id}`}>
                                <TableCell className="text-xs font-medium">
                                  {emp.employee_name}
                                  {emp.pay_basis === 'fixed' && <Badge variant="outline" className="text-xs ml-2 border-blue-500/30 text-blue-400">Fixed</Badge>}
                                  {emp.pay_basis === 'override' && <Badge variant="outline" className="text-xs ml-2 border-amber-500/30 text-amber-400" title={emp.override_reason || ''}>Override</Badge>}
                                </TableCell>
                                <TableCell className="text-xs">
                                  {emp.pay_basis === 'fixed' ? (
                                    <span className="text-blue-400">{fmt(emp.fixed_pay_period_amount)}/period</span>
                                  ) : (
                                    <>{fmt(emp.hourly_rate)}/hr</>
                                  )}
                                </TableCell>
                                <TableCell className="text-xs text-right tabular-nums">{emp.approved_hours}h</TableCell>
                                <TableCell className="text-xs text-right tabular-nums">{fmt(emp.gross_pay)}</TableCell>
                                <TableCell className="text-xs text-right tabular-nums">
                                  {fmt(emp.total_reimbursements)}
                                  {(emp.company_card_reimbursements || 0) > 0 && (
                                    <div className="text-xs text-purple-400" title="Company card expenses flow through a journal entry, not the paycheck.">
                                      + {fmt(emp.company_card_reimbursements)} CC
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell className="text-xs text-right font-bold tabular-nums">{fmt(emp.paycheck_total)}</TableCell>
                                <TableCell className="text-center">
                                  {emp.all_ts_resolved && emp.all_reimb_resolved ? (
                                    <CheckCircle2 className="h-4 w-4 text-emerald-400 mx-auto" />
                                  ) : (
                                    <div className="flex items-center justify-center gap-1">
                                      {!emp.all_ts_resolved && <Badge variant="outline" className="text-xs text-amber-400 border-amber-500/30">TS</Badge>}
                                      {!emp.all_reimb_resolved && <Badge variant="outline" className="text-xs text-amber-400 border-amber-500/30">RE</Badge>}
                                    </div>
                                  )}
                                </TableCell>
                                {isAdminOrOwner && !summary.locked && (
                                  <TableCell className="text-center">
                                    {emp.pay_basis === 'override' ? (
                                      <Button size="sm" variant="ghost" className="h-6 text-xs text-muted-foreground hover:text-destructive" onClick={() => clearOverride(emp.employee_id)} data-testid={`payroll-override-clear-${emp.employee_id}`}>
                                        <X className="h-3 w-3 mr-1" /> Clear
                                      </Button>
                                    ) : (
                                      <Button size="sm" variant="ghost" className="h-6 text-xs text-amber-400 hover:text-amber-300" onClick={() => openOverrideDialog(emp)} data-testid={`payroll-override-set-${emp.employee_id}`}>
                                        <Pencil className="h-3 w-3 mr-1" /> Override
                                      </Button>
                                    )}
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                            {summary.employees?.filter(e => e.total_hours > 0 || e.total_reimbursements > 0 || e.paycheck_total > 0 || (e.company_card_reimbursements || 0) > 0).length === 0 && (
                              <TableRow><TableCell colSpan={isAdminOrOwner ? 8 : 7} className="text-center text-muted-foreground text-xs py-4">No activity for this period</TableCell></TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  </>
                ) : null}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Owner Confirmation Dialog */}
      <Dialog open={confirmDialog === "owner"} onOpenChange={() => setConfirmDialog(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading">Owner Confirmation Required</DialogTitle>
            <DialogDescription>Acknowledge that all timesheets and reimbursements have been reviewed before advancing payroll.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            By confirming, you acknowledge that all timesheets and reimbursements have been reviewed
            and the payroll amounts are accurate.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(null)}>Cancel</Button>
            <Button onClick={() => advancePeriod({ owner_confirmed: true })} data-testid="confirm-owner-advance-btn">Confirm & Advance</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Acknowledgement Dialog - shown when backend returns 409 with a soft-block (rejected outstanding, unrated employees, etc.) */}
      <Dialog open={!!ackDialog} onOpenChange={(o) => { if (!o) setAckDialog(null); }}>
        <DialogContent className="bg-card border-border" data-testid="payroll-ack-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-amber-400">Confirm to Proceed</DialogTitle>
            <DialogDescription>Payroll can advance, but the following items need your acknowledgement first.</DialogDescription>
          </DialogHeader>
          {ackDialog && (
            <div className="space-y-2">
              <p className="text-sm text-foreground">{ackDialog.message}</p>
              {ackDialog.sample && (
                <div className="rounded-md border border-border bg-secondary/20 p-2.5">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Affected</p>
                  <p className="text-xs text-muted-foreground font-mono break-words">{ackDialog.sample}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAckDialog(null)}>Cancel</Button>
            <Button
              className="bg-amber-500 text-white hover:bg-amber-600"
              onClick={() => {
                if (!ackDialog) return;
                const ackMap = {
                  rejected_outstanding: 'acknowledge_rejected',
                  unrated_employees: 'acknowledge_unrated',
                  period_not_ended: 'early_close',
                  zero_payroll_amount: 'acknowledge_zero_payroll',
                };
                const flag = ackMap[ackDialog.code];
                if (!flag) return setAckDialog(null);
                advancePeriod({ ...(ackDialog.opts || {}), [flag]: true });
              }}
              data-testid="payroll-ack-confirm-btn"
            >
              Acknowledge & Advance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen Payroll Period Dialog - Owner-only, reverses paid_at on all entries */}
      <Dialog open={reopenDialog} onOpenChange={(o) => { if (!o) { setReopenDialog(false); setReopenReason(''); } }}>
        <DialogContent className="bg-card border-border" data-testid="reopen-payroll-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-amber-400">Reopen Closed Payroll Period</DialogTitle>
            <DialogDescription>This will revert the period from Paid / Distributed → Approved and clear paid_at on all stamped timesheets and reimbursements. The action is audit-logged.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Reason <span className="text-red-400">*</span></label>
            <textarea
              className="w-full min-h-[90px] text-sm bg-background border border-border rounded px-2 py-1.5"
              value={reopenReason}
              onChange={e => setReopenReason(e.target.value)}
              placeholder="E.g. 'Discovered unrated employee Jane Doe. Need to recalculate before reclosing'"
              data-testid="reopen-reason-input"
            />
            <p className="text-xs text-muted-foreground">Minimum 10 characters. Visible in the activity log.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReopenDialog(false); setReopenReason(''); }}>Cancel</Button>
            <Button
              className="bg-amber-500 text-white hover:bg-amber-600"
              onClick={reopenPeriod}
              disabled={reopenReason.trim().length < 10}
              data-testid="reopen-payroll-confirm-btn"
            >
              <Unlock className="h-3 w-3 mr-1" /> Reopen Period
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export by Project Dialog */}
      <Dialog open={projectExportOpen} onOpenChange={setProjectExportOpen}>
        <DialogContent data-testid="export-by-project-dialog">
          <DialogHeader>
            <DialogTitle>Export Payroll by Project</DialogTitle>
            <DialogDescription>Select a project and date range to export payroll data.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Project</label>
              <SearchableSelect
                options={projects.map(p => ({ value: p.id, label: `${p.project_number} - ${p.name}` }))}
                value={projectExportProjectId}
                onValueChange={setProjectExportProjectId}
                placeholder="Select project..."
                data-testid="project-export-select"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Billable Month</label>
              <input
                type="month"
                value={projectExportMonth}
                onChange={e => setProjectExportMonth(e.target.value)}
                className="flex h-10 w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
                data-testid="project-export-month"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProjectExportOpen(false)}>Cancel</Button>
            <Button onClick={exportByProject} disabled={projectExportLoading || !projectExportProjectId || !projectExportMonth} data-testid="project-export-download-btn">
              <Download className="h-3 w-3 mr-1" /> {projectExportLoading ? 'Exporting...' : 'Download PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payroll override dialog */}
      <Dialog open={!!overrideDialog} onOpenChange={(o) => !o && setOverrideDialog(null)}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="payroll-override-dialog">
          <DialogHeader>
            <DialogTitle>Override Payroll Amount</DialogTitle>
            <DialogDescription>
              Override the calculated gross pay for <span className="text-foreground font-medium">{overrideDialog?.employee_name}</span> on this pay period only. Employee-level settings are unchanged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">
              Calculated gross pay: <span className="font-mono">{fmt(overrideDialog?.current_amount)}</span>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Override Amount ($)</Label>
              <Input
                type="number" step="0.01" min="0"
                value={overrideDialog?.amount ?? ''}
                onChange={(e) => setOverrideDialog(d => ({ ...d, amount: e.target.value }))}
                className="bg-background border-border"
                data-testid="payroll-override-amount"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Reason *</Label>
              <Textarea
                value={overrideDialog?.reason ?? ''}
                onChange={(e) => setOverrideDialog(d => ({ ...d, reason: e.target.value }))}
                rows={3}
                placeholder="e.g. Bonus, correction for missed hours, agreed fixed payout..."
                className="bg-background border-border text-xs"
                data-testid="payroll-override-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideDialog(null)}>Cancel</Button>
            <Button onClick={submitOverride} data-testid="payroll-override-submit">Apply Override</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
